import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChevronDown, ChevronUp, LucideAngularModule, MapPin, Moon, Sun, Sunrise, Sunset } from 'lucide-angular';
import { BehaviorSubject, catchError, combineLatest, map, type Observable, of } from 'rxjs';
import type { DayTime } from '../../../types/day-time.type';
import type { AgendaItemModel, AgendaSection } from '../../core/models/middleware/agenda-item.model';
import type { EventType } from '../../core/models/middleware/event-item.model';
import type { LocationItemModel } from '../../core/models/middleware/location-item.model';
import { AgendaService } from '../../core/services/agenda.service';
import { CurrentTimeService } from '../../core/services/current-time.service';
import { BadgeComponent } from '../../shared/components/badge/badge.component';
import {
  DayNavigationComponent,
  type DayNavigationItem,
} from '../../shared/components/day-navigation/day-navigation.component';
import { LiveBadgeComponent } from '../../shared/components/live-badge/live-badge.component';
import { SectionHeadlineComponent } from '../../shared/components/section-headline/section-headline.component';
import { SharedCardComponent } from '../../shared/components/shared-card/shared-card.component';
import { EnforceNewLinePipe } from '../../shared/pipes/enforce-new-line.pipe';

@Component({
  selector: 'app-agenda',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LucideAngularModule,
    SharedCardComponent,
    SectionHeadlineComponent,
    DayNavigationComponent,
    LiveBadgeComponent,
    DatePipe,
    BadgeComponent,
    EnforceNewLinePipe,
  ],
  templateUrl: './agenda.component.html',
})
export class AgendaComponent implements OnInit {
  groupedAgendaSections$!: Observable<Map<number, AgendaSection[]>>;
  filteredSections$!: Observable<AgendaSection[]>;
  isMultiDay$!: Observable<boolean>;
  errorLoadingAgenda: string | null = null;
  dayItems: DayNavigationItem[] = [];
  currentSelectedDay = 1;
  itemStatusMap = new Map<string, boolean>();
  itemExpansionState = new Map<string, boolean>();
  readonly MapPin = MapPin;
  readonly ChevronDown = ChevronDown;
  readonly ChevronUp = ChevronUp;
  currentTimeService = inject(CurrentTimeService);
  private currentSelectedDay$ = new BehaviorSubject<number>(1);
  private readonly sectionConfig: Omit<AgendaSection, 'items'>[] = [
    {
      dayTime: 'Vormittag',
      title: 'Vormittag',
      iconComponent: Sunrise,
      iconColor: '#FFD300',
    },
    {
      dayTime: 'Mittag',
      title: 'Mittag',
      iconComponent: Sun,
      iconColor: '#3ABEF9',
    },
    {
      dayTime: 'Nachmittag',
      title: 'Nachmittag',
      iconComponent: Sunset,
      iconColor: '#f59e0b',
    },
    {
      dayTime: 'Abend',
      title: 'Abend',
      iconComponent: Moon,
      iconColor: '#5148E6',
    },
  ];
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private agendaService = inject(AgendaService);
  private readonly eventDocumentId = this.route.snapshot.params.eventDocumentId as string;
  private readonly eventType = this.route.snapshot.params.eventType as EventType;

  get isMultiDay(): boolean {
    return this.dayItems.length > 1;
  }

  ngOnInit(): void {
    this.errorLoadingAgenda = null;

    this.groupedAgendaSections$ = this.agendaService.getAgendaItems(this.eventDocumentId, this.eventType).pipe(
      map((items) => {
        const grouped = this.groupAndSortAgendaItems(items);
        // Only setup day navigation once
        if (this.dayItems.length === 0) {
          this.setupDayNavigation(grouped);
        }
        return grouped;
      }),
      catchError((err: unknown) => {
        console.error('Error loading agenda items:', err);
        this.errorLoadingAgenda = 'Fehler beim Laden der Termine. Bitte versuchen Sie es sp\u00e4ter erneut.';
        return of(new Map<number, AgendaSection[]>());
      })
    );

    this.isMultiDay$ = this.groupedAgendaSections$.pipe(
      map((grouped) => {
        // Multi-day event when grouped data doesn't have -1 (single day indicator) and has multiple days
        return !grouped.has(-1) && grouped.size > 1;
      })
    );

    this.filteredSections$ = combineLatest([this.groupedAgendaSections$, this.currentSelectedDay$]).pipe(
      map(([grouped, selectedDay]) => {
        const hasMultipleDays = !grouped.has(-1) && grouped.size > 1;

        if (hasMultipleDays) {
          return grouped.get(selectedDay) || [];
        } else {
          return grouped.get(-1) || [];
        }
      })
    );
  }

  onDaySelected(day: number): void {
    this.currentSelectedDay = day;
    this.currentSelectedDay$.next(day);
  }

  onItemStatusChange(itemId: string, status: { isCurrent: boolean }): void {
    this.itemStatusMap.set(itemId, !status.isCurrent);
  }

  isItemInactive(itemId: string): boolean {
    return this.itemStatusMap.get(itemId) || false;
  }

  getItemTimelineColor(item: AgendaItemModel, dayTime: DayTime): string {
    const dayTimeColors = {
      Vormittag: '#FCD34D',
      Mittag: '#3ABEF9',
      Nachmittag: '#F59E0B',
      Abend: '#5148E6',
    };

    const baseColor = dayTimeColors[dayTime];
    const currentTime = new Date();
    const now = currentTime.getTime();
    const itemStart = item.startTime.getTime();
    const itemEnd = item.endTime.getTime();

    // Check if item is in the past (ended)
    if (now > itemEnd) {
      return '#C4C7CC'; // Past - grey
    }

    // Check if item is currently active (happening right now)
    if (now >= itemStart && now <= itemEnd) {
      return baseColor; // Current - full color
    }

    // Item is in the future - 40% opacity
    return this.hexToRgba(baseColor, 0.4);
  }

  getItemTimelineColorObservable(item: AgendaItemModel, dayTime: DayTime): Observable<string> {
    const dayTimeColors = {
      Vormittag: '#FCD34D',
      Mittag: '#3ABEF9',
      Nachmittag: '#F59E0B',
      Abend: '#5148E6',
    };

    const baseColor = dayTimeColors[dayTime];

    return this.currentTimeService.getItemTimeStatus(item).pipe(
      map((status) => {
        if (status.isPast) {
          return '#C4C7CC'; // Past - grey
        }

        if (status.isCurrent) {
          return baseColor; // Current - full color
        }

        // Future - 40% opacity
        return this.hexToRgba(baseColor, 0.4);
      })
    );
  }

  getItemInactiveStatusObservable(item: AgendaItemModel): Observable<boolean> {
    return this.currentTimeService.getItemTimeStatus(item).pipe(map((status) => !status.isCurrent));
  }

  getItemPastStatusObservable(item: AgendaItemModel): Observable<boolean> {
    return this.currentTimeService.getItemTimeStatus(item).pipe(map((status) => status.isPast));
  }

  getItemCurrentStatusObservable(item: AgendaItemModel): Observable<boolean> {
    return this.currentTimeService.getItemTimeStatus(item).pipe(map((status) => status.isCurrent));
  }

  getItemBackgroundColorObservable(item: AgendaItemModel, dayTime: DayTime): Observable<string> {
    const dayTimeColors = {
      Vormittag: '#FCD34D',
      Mittag: '#3ABEF9',
      Nachmittag: '#F59E0B',
      Abend: '#5148E6',
    };

    const baseColor = dayTimeColors[dayTime];

    return this.currentTimeService.getItemTimeStatus(item).pipe(
      map((status) => {
        if (status.isCurrent) {
          // Active item - use base color with 10% opacity
          return this.hexToRgba(baseColor, 0.1);
        }
        // Non-active items - transparent
        return 'transparent';
      })
    );
  }

  showLocation(location: LocationItemModel) {
    this.router
      .navigate(['event', 'locations', this.eventType, this.eventDocumentId], {
        queryParams: {
          highlight: location.documentId,
        },
      })
      .then();
  }

  private groupAndSortAgendaItems(agendaItems: AgendaItemModel[]): Map<number, AgendaSection[]> {
    if (!agendaItems || agendaItems.length === 0) {
      return new Map<number, AgendaSection[]>();
    }

    const groupedByDay = new Map<number, Map<string, AgendaItemModel[]>>();
    if (agendaItems.every((item) => item.day !== undefined)) {
      for (const agendaItem of agendaItems) {
        if (agendaItem.day === undefined) continue;
        if (!groupedByDay.has(agendaItem.day)) {
          groupedByDay.set(agendaItem.day, new Map<string, AgendaItemModel[]>());
        }
        const dayTime = this.getDayTime(agendaItem.startTime);
        const dayMap = groupedByDay.get(agendaItem.day);
        if (!dayMap?.has(dayTime)) {
          dayMap?.set(dayTime, []);
        }
        dayMap?.get(dayTime)?.push(agendaItem);
      }
    } else {
      groupedByDay.set(-1, new Map<string, AgendaItemModel[]>());
      for (const agendaItem of agendaItems) {
        const dayTime = this.getDayTime(agendaItem.startTime);
        const dayMap = groupedByDay.get(-1);
        if (!dayMap?.has(dayTime)) {
          dayMap?.set(dayTime, []);
        }
        dayMap?.get(dayTime)?.push(agendaItem);
      }
    }

    const result: Map<number, AgendaSection[]> = new Map<number, AgendaSection[]>();
    groupedByDay.forEach((value, key) => {
      result.set(key, this.mapToAgendaSection(value));
    });
    return result;
  }

  private mapToAgendaSection(agendaItems: Map<string, AgendaItemModel[]>): AgendaSection[] {
    return this.sectionConfig
      .map(
        (config) =>
          ({
            ...config,
            items: agendaItems.get(config.dayTime),
          }) as AgendaSection
      )
      .filter((section) => !!section.items && section.items.length > 0);
  }

  private getDayTime(dateTime: Date): DayTime {
    const hour = dateTime.getHours();
    if (hour >= 0 && hour < 12) return 'Vormittag';
    if (hour >= 12 && hour < 14) return 'Mittag';
    if (hour >= 14 && hour < 18) return 'Nachmittag';
    return 'Abend';
  }

  private setupDayNavigation(groupedSections: Map<number, AgendaSection[]>): void {
    this.dayItems = [];

    if (groupedSections.has(-1)) {
      // Single day event
      this.dayItems = [];
      return;
    }

    // Multi-day event
    const sortedDays = Array.from(groupedSections.keys()).sort((a, b) => a - b);

    for (const day of sortedDays) {
      const sections = groupedSections.get(day) || [];
      const firstItem = sections[0]?.items?.[0];

      if (firstItem) {
        this.dayItems.push({
          day,
          date: firstItem.startTime,
          label: `Tag ${day}`,
        });
      }
    }

    if (this.dayItems.length > 0) {
      const optimalDay = this.findOptimalStartDay(groupedSections);
      this.currentSelectedDay = optimalDay;
      this.currentSelectedDay$.next(optimalDay);
    }
  }

  private findOptimalStartDay(groupedSections: Map<number, AgendaSection[]>): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for date comparison

    // First priority: Find day matching current date
    for (const dayItem of this.dayItems) {
      const dayDate = new Date(dayItem.date);
      dayDate.setHours(0, 0, 0, 0);

      if (dayDate.getTime() === today.getTime()) {
        // Check if this day has any future items
        if (this.dayHasFutureItems(groupedSections, dayItem.day)) {
          return dayItem.day;
        }
      }
    }

    // Second priority: Find next future day
    for (const dayItem of this.dayItems) {
      const dayDate = new Date(dayItem.date);
      dayDate.setHours(0, 0, 0, 0);

      if (dayDate.getTime() > today.getTime()) {
        return dayItem.day;
      }
    }

    // Third priority: If all days are in the past, return the last day
    if (this.dayItems.length > 0) {
      return this.dayItems[this.dayItems.length - 1].day;
    }

    // Fallback: Return first day
    return this.dayItems[0]?.day || 1;
  }

  private dayHasFutureItems(groupedSections: Map<number, AgendaSection[]>, day: number): boolean {
    const sections = groupedSections.get(day) || [];
    const now = new Date();

    for (const section of sections) {
      for (const item of section.items) {
        if (item.startTime.getTime() > now.getTime()) {
          return true;
        }
      }
    }

    return false;
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Checks if description needs expansion by detecting actual text overflow
   */
  needsDescriptionExpansion(item: AgendaItemModel): boolean {
    if (!item.description) return false;

    const charCount = item.description.length;
    const wordCount = item.description.split(/\s+/).length;
    const screenWidth = window.innerWidth;

    // Responsive thresholds based on screen width
    let charThreshold = 180;
    let wordThreshold = 25;

    if (screenWidth >= 1200) {
      charThreshold = 220;
      wordThreshold = 35;
    } else if (screenWidth >= 768) {
      charThreshold = 200;
      wordThreshold = 30;
    } else {
      charThreshold = 180;
      wordThreshold = 25;
    }

    if (charCount > charThreshold || wordCount > wordThreshold) {
      return true;
    }

    const longWords = item.description.split(/\s+/).some((word) => word.length > 30);
    if (longWords) {
      return true;
    }

    if (item.description.includes('\n') || item.description.includes('\r')) {
      return true;
    }

    const denseTextRatio = charCount / Math.max(wordCount, 1);
    if (denseTextRatio > 12) {
      return true;
    }

    if (item.description.includes('http') || item.description.includes('www')) {
      return true;
    }

    const words = item.description.split(/\s+/);
    const hasVeryLongWord = words.some((word) => word.length > 35);
    if (hasVeryLongWord) {
      return true;
    }

    return false;
  }

  /**
   * Toggles expansion state for specific agenda item
   */
  toggleDescription(itemId: string): void {
    const currentState = this.itemExpansionState.get(itemId) || false;
    this.itemExpansionState.set(itemId, !currentState);
  }

  /**
   * Returns expansion state for specific agenda item
   */
  isDescriptionExpanded(itemId: string): boolean {
    return this.itemExpansionState.get(itemId) || false;
  }
}
